/**
 * Decide cosa mostrare del tutorial, dato lo stato e la rotta.
 *
 * Logica pura, senza dipendenze da router e store: riceve quello che serve e
 * restituisce una risposta, come `setupGate` e `listiniRedirectFor`. È la
 * parte che, sbagliata, si vedrebbe solo addosso a un cliente vero.
 */
import { PANNELLI, RIFLETTORI, TUTORIAL_VERSION } from "@/config/onboarding";

/** Rotta su cui, e solo su cui, si apre il benvenuto. */
export const ROTTA_BENVENUTO = "home";

/** Lo stato di chi non ha mai visto niente. */
export function statoVuoto() {
  return {
    version: TUTORIAL_VERSION,
    welcomeDoneAt: null,
    skippedAt: null,
    seen: [],
  };
}

function dataOppureNull(valore) {
  return typeof valore === "string" && !Number.isNaN(Date.parse(valore))
    ? valore
    : null;
}

/**
 * Normalizza quello che è arrivato dal server.
 *
 * Tutto ciò che non si riconosce vale «mai visto»: un JSON storto o di una
 * versione che non è la nostra non deve rompere la home, e rifare il giro è
 * il danno minore fra i due.
 */
export function leggiStato(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw))
    return statoVuoto();
  if (raw.version !== TUTORIAL_VERSION) return statoVuoto();
  if (!Array.isArray(raw.seen)) return statoVuoto();

  return {
    version: TUTORIAL_VERSION,
    welcomeDoneAt: dataOppureNull(raw.welcomeDoneAt),
    skippedAt: dataOppureNull(raw.skippedAt),
    seen: raw.seen.filter((p) => typeof p === "string"),
  };
}

/** Lo stesso stato con un passo in più fra quelli visti. */
export function conPassoVisto(stato, passo) {
  const base = leggiStato(stato);
  if (base.seen.includes(passo)) return base;
  return { ...base, seen: [...base.seen, passo] };
}

/**
 * Il testo spezzato in frasi, una per riga.
 *
 * [sc-46314, review 48629] «se c'è il punto, andare sempre a capo». Un
 * paragrafo compatto dentro una finestra che si legge in due secondi non lo
 * legge nessuno: tre righe corte sì. Il taglio è sul punto seguito da uno
 * spazio, non su ogni punto, altrimenti si spezzerebbero anche le sigle e i
 * decimali. Niente lookbehind nell'espressione regolare: non passa da Babel,
 * e i Safari vecchi la rifiutano al caricamento del bundle.
 */
export function inFrasi(testo) {
  if (typeof testo !== "string") return [];
  const frasi = [];
  let inizio = 0;
  for (let i = 0; i < testo.length; i++) {
    if (testo[i] !== ".") continue;
    const dopo = testo[i + 1];
    if (dopo !== undefined && !/\s/.test(dopo)) continue;
    const frase = testo.slice(inizio, i + 1).trim();
    if (frase) frasi.push(frase);
    inizio = i + 1;
  }
  const coda = testo.slice(inizio).trim();
  if (coda) frasi.push(coda);
  return frasi;
}

/** Chi non deve vedere niente del tutorial, in nessun caso. */
function fuoriGioco({ user, configured, readOnly }) {
  if (!user) return true;
  // L'amministratore di crmstart non ha un gestionale da imparare.
  if (user.companyId === null) return true;
  // Prima viene la configurazione iniziale: un tutorial sopra quella
  // procedura sarebbe una finestra sopra un'altra finestra.
  if (!configured) return true;
  // A prova finita non c'è più niente da iniziare.
  if (readOnly) return true;
  return false;
}

/** Chi ha saltato non vede più né benvenuto né pannellini. */
function haSaltato(stato) {
  return Boolean(stato && stato.skippedAt);
}

export function benvenutoDaMostrare(ctx) {
  if (fuoriGioco(ctx)) return false;
  if (ctx.routeName !== ROTTA_BENVENUTO) return false;
  const stato = leggiStato(ctx.stato);
  if (haSaltato(stato)) return false;
  return !stato.welcomeDoneAt;
}

export function pannelloPerRotta(ctx) {
  if (fuoriGioco(ctx)) return null;
  const pannello = PANNELLI[ctx.routeName];
  if (!pannello) return null;
  const stato = leggiStato(ctx.stato);
  if (haSaltato(stato)) return null;
  if (stato.seen.includes(ctx.routeName)) return null;
  return { ...pannello, chiave: ctx.routeName };
}

export function riflettorePerRotta(ctx) {
  if (fuoriGioco(ctx)) return null;
  const riflettore = RIFLETTORI[ctx.routeName];
  if (!riflettore) return null;
  const stato = leggiStato(ctx.stato);
  if (haSaltato(stato)) return null;
  if (stato.seen.includes(riflettore.chiave)) return null;
  return riflettore;
}

/**
 * Le chiavi di stato che riguardano una rotta.
 *
 * Sono due mondi diversi che si incontrano qui: il pannellino si segna col
 * nome della rotta, il riflettore con la chiave del suo registro. Chi chiede
 * «spiegami questa pagina» le vuole tutte e due.
 */
function chiaviDiRotta(routeName) {
  const chiavi = [];
  if (PANNELLI[routeName]) chiavi.push(routeName);
  const riflettore = RIFLETTORI[routeName];
  if (riflettore) chiavi.push(riflettore.chiave);
  return chiavi;
}

/**
 * C'è un giro da rifare su questa rotta?
 *
 * Solo dove c'è un riflettore, cioè sulle tre schede da compilare: lì il giro
 * è di cinque o sei passi. Sulle pagine del menù il tutorial è il pannellino
 * in cima, una riga sola: una voce che promette di spiegare la pagina e
 * rimette una riga promette più di quanto mantenga, e chi quella riga la
 * rivuole ha «Rivedi il tutorial». In home vale lo stesso, e in più è il
 * punto da cui «Rivedi il tutorial» riparte.
 */
export function haSpiegazione(routeName) {
  return Boolean(RIFLETTORI[routeName]);
}

/**
 * Lo stesso stato, ma con i passi di questa rotta da rivedere.
 *
 * [sc-46314] È l'inverso di `conPassoVisto`, per chi vuole rileggere la
 * spiegazione della pagina in cui si trova senza rifare il giro da capo -
 * e soprattutto senza essere portato via da un modulo mezzo compilato.
 *
 * Il salto si toglie: saltare vuol dire «non interrompermi», non «non
 * spiegarmi mai più». Ma la data passa su `welcomeDoneAt`, altrimenti al
 * prossimo passaggio in home tornerebbe la finestra di benvenuto, che non è
 * quello che è stato chiesto.
 */
export function senzaPassiDiRotta(stato, routeName) {
  const base = leggiStato(stato);
  const chiavi = chiaviDiRotta(routeName);
  if (chiavi.length === 0) return base;
  return {
    ...base,
    welcomeDoneAt: base.welcomeDoneAt ?? base.skippedAt,
    skippedAt: null,
    seen: base.seen.filter((passo) => !chiavi.includes(passo)),
  };
}

export default {
  statoVuoto,
  leggiStato,
  conPassoVisto,
  inFrasi,
  benvenutoDaMostrare,
  pannelloPerRotta,
  riflettorePerRotta,
  haSpiegazione,
  senzaPassiDiRotta,
};
